import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import { AssignControl } from "./AssignControl";
import { getSource, sourceKey, type SourceDetail as Detail } from "./api";
import { backSince, daysBetween, displayName, personLabel, reasonWords, shortDate } from "./format";
import { SourceName } from "./SourceName";
import { StatusGlyph } from "./StatusGlyph";

/**
 * S2 Source detail — a drill-in inside Sources, not a tab (UX §3). Reached from a
 * row, or cold from C3's Slack link `?source=<id>` (seam S7).
 *
 * Mobile order is the DOM order: banner, then the commit panel, then the rest.
 *
 * Three things R1 cannot show, said rather than faked (C10 dev spec §6):
 * a committed back-by date and the Commit write (C3 not live), the restated
 * period count (C3), and the dependent apps (C1-D9).
 */
export function SourceDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const query = useQuery({ queryKey: sourceKey(id), queryFn: () => getSource(id) });

  return (
    <div className="flex max-w-4xl flex-col gap-4">
      <button type="button" data-nonmutating onClick={onBack} className="self-start text-xs text-ks-grey-1 hover:underline">
        &lt; Back to all sources
      </button>
      {query.isPending ? (
        <>
          <Skeleton className="h-7 w-80" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-40 w-full" />
        </>
      ) : query.isError ? (
        <Alert>
          <AlertDescription>
            <b>We couldn't open this source.</b> It may have been removed from the registry. Go back to all sources and pick it again.
          </AlertDescription>
        </Alert>
      ) : (
        <DetailBody d={query.data.data} />
      )}
    </div>
  );
}

function DetailBody({ d }: { d: Detail }) {
  const state = d.health.state;
  const lastCheck = d.checks[0]?.check_date ?? d.health.check_date;
  const darkDays = daysBetween(d.dark_since, lastCheck);
  const restoredSince = state === "ok" ? backSince(d.checks) : null;
  const screen = state === "dark" ? "s2-source-dark" : restoredSince ? "s2-source-restored" : `s2-${state}`;

  return (
    <div className="flex flex-col gap-4" data-screen={screen}>
      <h2 className="m-0 flex items-center gap-3 text-[19px] font-bold text-ks-ink">
        <StatusGlyph state={state} />
        <SourceName name={displayName(d)} />
      </h2>

      {state === "dark" && (
        <Alert>
          <AlertDescription>
            <b>{`Dark since ${d.dark_since ? shortDate(d.dark_since) : "its last check"}${darkDays !== null ? `, ${darkDays} ${darkDays === 1 ? "day" : "days"}` : ""}.`}</b>{" "}
            No date committed yet.
          </AlertDescription>
        </Alert>
      )}
      {restoredSince && (
        <div className="rounded-lg border border-ks-grey-3 px-4 py-3 text-sm text-ks-ink">
          <b>{`Back since ${shortDate(restoredSince)}.`}</b>{" "}
          {d.restated_periods !== null
            ? `${d.restated_periods} published periods were restated, and the people who read them have been told.`
            : "Restated periods are tracked once escalation is live."}
        </div>
      )}
      {state === "unknown" && (
        <div className="rounded-lg border border-ks-grey-3 px-4 py-3 text-sm text-ks-ink">
          <b>Couldn't check.</b> {`${reasonWords(d.health.reason)}. Nothing here is confirmed healthy.`}
        </div>
      )}

      {restoredSince ? <RestatedPanel d={d} /> : <CommitPanel d={d} />}

      <h3 className="mt-2 text-base font-bold text-ks-ink">This feeds</h3>
      <div className="overflow-hidden rounded-lg border border-ks-grey-4">
        <KpiRow kpis={d.dependent_kpis} />
        <div className="px-4 py-3 text-sm text-ks-grey-1" data-apps-line>
          {d.dependent_apps === null ? "Apps: not yet recorded." : `${d.dependent_apps.length} apps: ${d.dependent_apps.join(", ")}`}
        </div>
      </div>

      {!restoredSince && (
        <>
          <div className="mt-2 flex items-center justify-between gap-3">
            <h3 className="text-base font-bold text-ks-ink">Recent checks</h3>
            <NotYetButton>Edit checks</NotYetButton>
          </div>
          <div className="overflow-hidden rounded-lg border border-ks-grey-4">
            {!d.checks_readable ? (
              <p className="px-4 py-3 text-sm text-ks-ink">We couldn't load the check history. Retry in a moment.</p>
            ) : d.checks.length === 0 ? (
              <p className="px-4 py-3 text-sm text-ks-ink">First check runs at 07:00 tomorrow.</p>
            ) : (
              <ul>
                {d.checks.map((c, i) => (
                  <li key={`${c.check_date}-${c.reason}`} className="flex items-center gap-3 border-b border-ks-grey-4 px-4 py-3 last:border-b-0">
                    <StatusGlyph state={c.state} />
                    <span className="min-w-0 flex-1 text-sm text-ks-ink">{shortDate(c.check_date)}</span>
                    <span className="text-[13px] text-ks-grey-1">{c.state === "ok" ? "within its usual range" : reasonWords(c.reason)}</span>
                    {i === 0 && c.state === "dark" && <NotYetButton>Not a problem?</NotYetButton>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="text-xs text-ks-grey-1" id="kdi-not-yet">
            Editing checks and marking a false alarm open once the monitor accepts changes.
          </p>
        </>
      )}

      <p className="text-xs text-ks-grey-1">
        Expected {(d.cadence || "on no recorded cadence").toLowerCase()} · checked from outside the pipeline
      </p>
    </div>
  );
}

/**
 * D-C10-3: rendered, not wired. The monitor (C2) has no write for these in R1, so
 * the control is shown closed and the line under Recent checks says why.
 */
function NotYetButton({ children }: { children: string }) {
  return (
    <Button size="sm" variant="outline" className="shrink-0" disabled aria-describedby="kdi-not-yet">
      {children}
    </Button>
  );
}

/** "11 KPIs incl. …" + See all, which lists every KPI this source feeds. Reads only. */
function KpiRow({ kpis }: { kpis: string[] }) {
  const [all, setAll] = useState(false);
  return (
    <div className="flex flex-col gap-2 border-b border-ks-grey-4 px-4 py-3 text-sm text-ks-ink">
      <div className="flex items-center gap-3">
        <span className="min-w-0 flex-1 truncate">{kpiLine(kpis)}</span>
        {kpis.length > 3 && (
          <Button size="sm" variant="outline" className="shrink-0" data-nonmutating aria-expanded={all} onClick={() => setAll((v) => !v)}>
            {all ? "Show fewer" : "See all"}
          </Button>
        )}
      </div>
      {all && <p className="text-[13px] text-ks-grey-1">{kpis.join(", ")}</p>}
    </div>
  );
}

function kpiLine(kpis: string[]): string {
  if (kpis.length === 0) return "No KPIs recorded as reading this source.";
  const head = kpis.slice(0, 3).join(", ");
  return `${kpis.length} ${kpis.length === 1 ? "KPI" : "KPIs"}${kpis.length > 3 ? " incl." : ":"} ${head}`;
}

/** Back by + Commit (action #2). R1: C3 is not live, so the write is closed and says why. */
function CommitPanel({ d }: { d: Detail }) {
  if (!d.owner_email) {
    return (
      <div className="flex flex-col gap-2 rounded-lg border border-ks-grey-4 p-4">
        <p className="text-sm font-bold text-ks-ink">Assign an owner first</p>
        <p className="text-xs text-ks-grey-1">A return date is committed by the source's owner. Nobody owns this one yet.</p>
        <AssignControl sourceKey={d.source_key} label="Assign an owner" />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-ks-grey-4 p-4">
      <label className="text-sm font-bold text-ks-ink" htmlFor="kdi-back-by">Back by</label>
      <div className="flex flex-wrap items-center gap-2">
        <Input id="kdi-back-by" type="date" className="w-44" disabled value={d.commitment.back_by ?? ""} readOnly />
        <Button disabled={!d.commitment.available}>Commit this date</Button>
      </div>
      {!d.commitment.available && (
        <p className="text-xs text-ks-grey-1">Committing a date opens once escalation is live.</p>
      )}
      <p className="text-xs text-ks-grey-1">{personLabel(d.owner_email)} owns this source.</p>
    </div>
  );
}

function RestatedPanel({ d }: { d: Detail }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-ks-grey-4 p-4">
      <p className="text-sm font-bold text-ks-ink">Restated periods</p>
      <p className="text-xs text-ks-grey-1">
        {d.restated_periods !== null
          ? "These periods were published while this feed was dark. Their values have changed."
          : "Which published periods changed is recorded once escalation is live."}
      </p>
      <p className="border-l-[3px] border-ks-purple-2 bg-ks-purple-4 px-3 py-2 text-[13px] text-ks-ink">
        Where nothing backfills, the period is marked permanently incomplete instead, with its reason. It never reads as a zero.
      </p>
    </div>
  );
}
